/**
 * Protocolo de rede. FONTE UNICA das mensagens, importado pelas DUAS pontas:
 * o cliente (src/net/NetClient.js) e o servidor (server/net/gameserver.js).
 *
 * Por que existe um arquivo so para isto: mensagem de rede e o unico lugar do
 * sistema onde as duas pontas precisam concordar caractere por caractere. Se
 * cada lado escrever seu proprio `JSON.stringify({ t: 'state', ... })`, um dia
 * alguem renomeia um campo em um lado so e o bug aparece como "o jogador remoto
 * fica parado na origem" - sintoma que nao aponta para lugar nenhum.
 *
 * Aqui NAO ha logica de jogo. So construir, validar forma e ler mensagem.
 *
 * Este arquivo e PURO: zero import a nao ser do netconfig (tambem puro).
 * Roda no browser e no Node sem transpilar.
 */

import { NET, FLAG } from './netconfig.js';

/* ========================================================================== */
/* tipos                                                                       */
/* ========================================================================== */

/** Cliente -> servidor. */
export const C2S = {
  HELLO: 'hello',
  JOIN: 'join',
  LEAVE: 'leave',
  STATE: 'state',
  SHOT: 'shot',
  HIT: 'hit',
  CHAT: 'chat',
  PONG: 'pong'
};

/** Servidor -> cliente. */
export const S2C = {
  WELCOME: 'welcome',
  JOINED: 'joined',
  PLAYER_JOIN: 'playerJoin',
  PLAYER_LEAVE: 'playerLeave',
  SNAPSHOT: 'snapshot',
  SHOT: 'shot',
  DAMAGE: 'damage',
  KILL: 'kill',
  RESPAWN: 'respawn',
  SCORE: 'score',
  MATCH_END: 'matchEnd',
  CHAT: 'chat',
  ERROR: 'error',
  PING: 'ping'
};

/* ========================================================================== */
/* codec                                                                       */
/* ========================================================================== */

/**
 * Serializa. Mantido como funcao (e nao JSON.stringify solto pelo codigo) para
 * existir UM lugar onde trocar o formato do fio no futuro sem cacar chamadas.
 */
export function encode(msg) {
  return JSON.stringify(msg);
}

/**
 * Desserializa com desconfianca: o que chega do fio pode ser qualquer coisa.
 * Devolve `null` para tudo que nao seja um objeto com `t` string - quem chama
 * so precisa testar null, nunca envolver em try/catch.
 *
 * @param {string|ArrayBuffer|Uint8Array} raw
 * @returns {object|null}
 */
export function decode(raw) {
  let text = raw;
  if (typeof text !== 'string') {
    try {
      text = new TextDecoder().decode(raw);
    } catch {
      return null;
    }
  }
  if (text.length > NET.MAX_MESSAGE_BYTES) return null;

  let obj;
  try {
    obj = JSON.parse(text);
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;
  if (typeof obj.t !== 'string' || obj.t.length === 0) return null;
  return obj;
}

/* ========================================================================== */
/* construtores - cliente -> servidor                                          */
/* ========================================================================== */

export function hello(token) {
  return { t: C2S.HELLO, token: String(token || '') };
}

/**
 * `map` viaja junto porque a sala e POR MAPA: o cliente constroi o cenario no
 * boot, entao entrar numa sala de outro mapa deixaria ele atirando em jogadores
 * que estao num lugar que ele nao esta desenhando.
 */
export function join(room, map) {
  return {
    t: C2S.JOIN,
    room: room == null ? null : String(room),
    map: map == null ? null : String(map)
  };
}

export function leave() {
  return { t: C2S.LEAVE };
}

/**
 * Estado do jogador local. Coordenadas arredondadas em 3 casas (milimetro) e
 * angulos em 4: no fio, precisao alem disso e byte gasto para descrever ruido
 * de ponto flutuante que ninguem enxerga na tela.
 */
export function state(seq, pos, yaw, pitch, flags, weaponIndex) {
  return {
    t: C2S.STATE,
    seq: seq | 0,
    p: [round3(pos[0]), round3(pos[1]), round3(pos[2])],
    y: round4(yaw),
    pt: round4(pitch),
    f: flags | 0,
    w: weaponIndex | 0
  };
}

export function shot(seq, weaponId, origin, dir) {
  return {
    t: C2S.SHOT,
    seq: seq | 0,
    w: String(weaponId || ''),
    o: [round3(origin[0]), round3(origin[1]), round3(origin[2])],
    d: [round4(dir[0]), round4(dir[1]), round4(dir[2])]
  };
}

/**
 * Reclamacao de acerto. O cliente NAO diz quanto de dano deu - so diz em quem
 * acertou, onde e com o que. Quem calcula dano e o servidor, com a tabela de
 * armas. Mandar o dano daqui seria entregar a vida do adversario ao cliente.
 */
export function hit(seq, targetId, part, weaponId, dist) {
  return {
    t: C2S.HIT,
    seq: seq | 0,
    target: targetId | 0,
    part: String(part || 'chest'),
    w: String(weaponId || ''),
    dist: round3(dist)
  };
}

export function chat(text) {
  return { t: C2S.CHAT, text: String(text || '').slice(0, 200) };
}

export function pong(id) {
  return { t: C2S.PONG, id: id | 0 };
}

/* ========================================================================== */
/* construtores - servidor -> cliente                                          */
/* ========================================================================== */

export function welcome(you) {
  return {
    t: S2C.WELCOME,
    you,
    tickRate: NET.TICK_RATE,
    snapshotRate: NET.SNAPSHOT_RATE
  };
}

export function joined(room, players, youId) {
  return { t: S2C.JOINED, room, players, you: youId | 0 };
}

export function playerJoin(player) {
  return { t: S2C.PLAYER_JOIN, player };
}

export function playerLeave(id) {
  return { t: S2C.PLAYER_LEAVE, id: id | 0 };
}

/** `ts` em MILISSEGUNDOS do relogio do servidor (Date.now()). */
export function snapshot(ts, players) {
  return { t: S2C.SNAPSHOT, ts, players };
}

export function remoteShot(byId, origin, dir, weaponId) {
  return { t: S2C.SHOT, by: byId | 0, o: origin, d: dir, w: weaponId };
}

export function damage(byId, amount, hp, part) {
  return { t: S2C.DAMAGE, by: byId | 0, amount, hp, part };
}

export function kill(killerId, victimId, weaponId, headshot) {
  return {
    t: S2C.KILL,
    killer: killerId | 0,
    victim: victimId | 0,
    w: weaponId,
    headshot: !!headshot
  };
}

export function respawn(id, pos, yaw, hp) {
  return { t: S2C.RESPAWN, id: id | 0, p: pos, y: yaw, hp };
}

export function score(players, timeLeft) {
  return { t: S2C.SCORE, players, timeLeft };
}

export function matchEnd(standings, matchId) {
  return { t: S2C.MATCH_END, standings, matchId: matchId == null ? null : matchId };
}

export function chatFrom(fromId, nickname, text) {
  return { t: S2C.CHAT, from: fromId | 0, nickname, text };
}

export function error(code, message) {
  return { t: S2C.ERROR, code: String(code), message: String(message || '') };
}

export function ping(id) {
  return { t: S2C.PING, id: id | 0 };
}

/* ========================================================================== */
/* validacao de forma                                                          */
/* ========================================================================== */

/**
 * Confere se uma mensagem JA DECODIFICADA tem a forma esperada do seu tipo.
 * Isto NAO julga se o conteudo e plausivel (isso e do validate.js do servidor):
 * so garante que quem for ler `msg.p[0]` nao vai achar `undefined` ou uma string.
 *
 * @returns {boolean}
 */
export function isValidC2S(msg) {
  if (!msg || typeof msg.t !== 'string') return false;
  switch (msg.t) {
    case C2S.HELLO:
      return typeof msg.token === 'string' && msg.token.length > 0 && msg.token.length < 4096;
    case C2S.JOIN: {
      const salaOk = msg.room === null || msg.room === undefined || typeof msg.room === 'string';
      const mapaOk = msg.map === null || msg.map === undefined || typeof msg.map === 'string';
      return salaOk && mapaOk;
    }
    case C2S.LEAVE:
      return true;
    case C2S.STATE:
      return isVec3(msg.p) && isNum(msg.y) && isNum(msg.pt) && isInt(msg.f) && isInt(msg.w);
    case C2S.SHOT:
      return typeof msg.w === 'string' && isVec3(msg.o) && isVec3(msg.d);
    case C2S.HIT:
      return isInt(msg.target) && typeof msg.part === 'string' &&
             typeof msg.w === 'string' && isNum(msg.dist) && msg.dist >= 0;
    case C2S.CHAT:
      return typeof msg.text === 'string' && msg.text.length > 0 && msg.text.length <= 200;
    case C2S.PONG:
      return isInt(msg.id);
    default:
      return false;
  }
}

/* ========================================================================== */
/* auxiliares                                                                  */
/* ========================================================================== */

/** Le o flag de um estado. Existe para ninguem espalhar `& 1` pelo codigo. */
export function hasFlag(flags, bit) {
  return ((flags | 0) & bit) !== 0;
}

/** Monta o campo `f` a partir de booleanos. */
export function makeFlags({ crouch, ads, sprint, reload, dead, grounded } = {}) {
  let f = 0;
  if (crouch) f |= FLAG.CROUCH;
  if (ads) f |= FLAG.ADS;
  if (sprint) f |= FLAG.SPRINT;
  if (reload) f |= FLAG.RELOAD;
  if (dead) f |= FLAG.DEAD;
  if (grounded) f |= FLAG.GROUNDED;
  return f;
}

function isNum(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function isInt(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function isVec3(v) {
  return Array.isArray(v) && v.length === 3 && isNum(v[0]) && isNum(v[1]) && isNum(v[2]);
}

function round3(v) {
  return Math.round((Number(v) || 0) * 1000) / 1000;
}

function round4(v) {
  return Math.round((Number(v) || 0) * 10000) / 10000;
}

export { FLAG };
